/**
 * The ledger — why each balance component moved.
 *
 * **It is not a custody record, because there is no custody** (§F26l). The USDC
 * never leaves the user's own wallet, so Arbitrum is the audit trail for the
 * money and always was better evidence than an endpoint could be. What the chain
 * cannot say is *why* — and it says nothing at all about `order_locked`, which
 * has no on-chain existence: orders rest off-chain, nothing reserves that USDC.
 *
 * **Rows are grouped by `journal_id`, and the group is the unit that matters.**
 * `openapi`: entries sharing one sum to exactly zero. A ledger rendered as a
 * flat list of deltas asks the user to believe that; rendered as journals, with
 * the sum shown, it lets them check. `journalSum()` is that check, in `bigint`
 * micro-USDC — a float in the one component whose whole job is exactness would
 * be absurd.
 *
 * **Zero-valued fee rows are shown.** They are posted on every fill and carry
 * `0.000000` while `taker_fee_rate` is `0.0000`; hiding them would teach the
 * user that fees do not appear, and the day the rate moves nothing here would
 * have to change.
 */

import { memo, useMemo } from 'react'
import { EmptyState, Pill } from '@/components/primitives'
import { cx } from '@/lib/cx'
import { parseFixed, USDC_DP } from '@/lib/decimal'
import { formatSignedUsdc, formatUsdc, formatUtcTime } from '@/lib/format'

export interface LedgerEntry extends Record<string, unknown> {
  entry_id?: string
  journal_id?: string
  seq?: number
  account_code?: string
  delta?: string
  balance_after?: string | null
  ref_type?: string
  market_id?: string | null
  memo?: string | null
  created_at_ms?: number
}

/** The user-facing name of each account. `USER_` prefixes are plumbing. */
const ACCOUNT_LABEL: Record<string, string> = {
  USER_AVAILABLE: 'Available',
  USER_ORDER_LOCKED: 'Reserved',
  USER_MARKET_COLLATERAL: 'In positions',
  FEE_REVENUE: 'Fees',
  REBATE_EXPENSE: 'Rebates',
  SETTLEMENT_CLEARING: 'Settlement pool',
}

interface Journal {
  id: string
  refType: string
  marketId: string | null
  memo: string | null
  atMs: number
  entries: LedgerEntry[]
  balances: boolean
}

export const LedgerTable = memo(function LedgerTable({
  rows,
  className,
}: {
  rows: readonly LedgerEntry[]
  className?: string
}) {
  // Grouped in the order the rows arrived, which is newest-first — so a `Map`
  // preserves it and nothing has to re-sort. A ledger that re-ordered itself
  // between pages would be the one screen where that is unforgivable.
  const journals = useMemo<Journal[]>(() => {
    const byId = new Map<string, Journal>()
    for (const entry of rows) {
      const id = typeof entry.journal_id === 'string' ? entry.journal_id : entry.entry_id
      if (typeof id !== 'string') continue
      const existing = byId.get(id)
      if (existing) {
        existing.entries.push(entry)
        continue
      }
      // A journal's own legs read in the order they were posted, which is
      // `seq` ascending — the rows arrive newest-first, so within a group they
      // would otherwise render backwards and a fill would appear to credit the
      // position before debiting the reservation.

      byId.set(id, {
        id,
        refType: String(entry.ref_type ?? '—'),
        marketId: typeof entry.market_id === 'string' ? entry.market_id : null,
        memo: null,
        atMs: typeof entry.created_at_ms === 'number' ? entry.created_at_ms : 0,
        entries: [entry],
        balances: true,
      })
    }
    for (const journal of byId.values()) {
      journal.entries.sort((a, b) => seqOf(a) - seqOf(b))
      // **The header memo is the *first* posting's, and only after sorting.**
      // Rows arrive newest-first, so taking it as the group was built named a
      // fill journal after its last leg — a trade headed "Taker fee" — and then
      // marked every other leg's memo as different from it, printing the real
      // one twice in the rows below.
      const first = journal.entries[0]
      journal.memo = typeof first?.memo === 'string' ? first.memo : null
    }
    return [...byId.values()]
  }, [rows])

  if (journals.length === 0) {
    return (
      <EmptyState>
        Nothing has moved in the last 7 days. Older results are under Settled.
      </EmptyState>
    )
  }

  return (
    <div className={cx('flex flex-col', className)} data-testid="ledger">
      {journals.map((journal) => {
        const sum = journalSum(journal.entries)
        return (
          <section
            key={journal.id}
            data-journal-id={journal.id}
            data-balanced={sum === 0n ? 'true' : 'false'}
            className="border-b border-border-subtle px-3 py-2"
          >
            <header className="flex flex-wrap items-baseline gap-2">
              <Pill tone="neutral">{journal.refType}</Pill>
              <span className="text-body-sm text-text-primary">{journal.memo ?? journal.id}</span>
              <span className="ml-auto text-caption text-text-tertiary">
                {journal.atMs ? formatUtcTime(journal.atMs) : '—'}
              </span>
            </header>

            <dl className="mt-1_5 flex flex-col gap-0_5">
              {journal.entries.map((entry) => (
                <div key={String(entry.entry_id)} className="flex items-baseline gap-2 text-caption">
                  <dt className="w-[calc(144*var(--ud-u))] shrink-0 text-text-tertiary">
                    {ACCOUNT_LABEL[String(entry.account_code)] ?? String(entry.account_code)}
                  </dt>
                  <dd className="ud-num w-[calc(112*var(--ud-u))] shrink-0 text-right text-text-secondary">
                    {typeof entry.delta === 'string' ? formatSignedUsdc(entry.delta) : '—'}
                  </dd>
                  <dd className="ud-num text-text-tertiary">
                    {typeof entry.balance_after === 'string' ?
                      `→ ${formatUsdc(entry.balance_after)}`
                    : ''}
                  </dd>
                  {entry.memo && entry.memo !== journal.memo ? (
                    <dd className="truncate text-text-tertiary" title={entry.memo}>
                      {entry.memo}
                    </dd>
                  ) : null}
                </div>
              ))}
            </dl>

            {/*
              Only ever rendered when the promise is broken. A journal whose legs
              do not sum to zero is a ledger the user cannot rely on, and saying
              so is the only honest render — the alternative is a page that looks
              correct and is not.
            */}
            {sum !== 0n ? (
              <p role="status" className="mt-1 text-caption text-danger-text">
                These postings do not sum to zero. The venue owes an explanation.
              </p>
            ) : null}
          </section>
        )
      })}
    </div>
  )
})

/**
 * The legs added, exactly.
 *
 * A leg that will not parse contributes nothing **and** makes the journal
 * unbalanced, which is the honest pair: the failure is visible rather than
 * quietly absorbed into a total that still looks right.
 */
function seqOf(entry: LedgerEntry): number {
  return typeof entry.seq === 'number' ? entry.seq : 0
}

function journalSum(entries: readonly LedgerEntry[]): bigint {
  let total = 0n
  for (const entry of entries) {
    if (typeof entry.delta !== 'string') return 1n
    try {
      total += parseFixed(entry.delta, USDC_DP)
    } catch {
      return 1n
    }
  }
  return total
}
